/**
 * AdventureScreen - Gestion des histoires d'aventure (admin)
 * Permet de créer, éditer, publier et archiver des stories, biomes et niveaux.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Grid,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Map as MapIcon,
  Terrain as TerrainIcon,
  Refresh as RefreshIcon,
  OpenWith as LayoutIcon,
  Image as ImageIcon,
  FileDownload as FileDownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import BoxPositionnerEditor from '../../components/admin/BoxPositionnerEditor';
import AdventureStoryAssetsPanel from '../../components/admin/AdventureStoryAssetsPanel';
import { getAssetUrl } from '../../utils/axios-config';
import { wrapLayout, layoutBoxCount, unwrapWidthFactor } from '../../utils/adventureLayoutFormat';
import {
  getAdminScenarios,
  getAdminStories,
  getAdminStory,
  getAdminStoryAssets,
  getAdminSealCandidates,
  createAdminStory,
  updateAdminStory,
  publishAdminStory,
  archiveAdminStory,
  getAdminStoryAudit,
  createAdminBiome,
  updateAdminBiome,
  deleteAdminBiome,
  createAdminLevel,
  updateAdminLevel,
  deleteAdminLevel,
  updateAdminStoryPresentation,
  publishAdminStoryPresentation,
  updateAdminBiomePresentation,
  publishAdminBiomePresentation,
  exportAdminStoryPack,
  importAdminStoryPack,
} from '../../services/adminService';
import { getScenarioThemes, getScenarioTempos } from '../../config/shared_config';

// ─── Constantes ──────────────────────────────────────────────────────────────

const STATUT_COLORS = {
  draft: 'default',
  published: 'success',
  archived: 'error',
};

const STATUT_LABELS = {
  draft: 'Brouillon',
  published: 'Publiée',
  archived: 'Archivée',
};

const DIFFICULTE_LABELS = {
  facile: 'Facile',
  moyen: 'Moyen',
  difficile: 'Difficile',
};

// Public visé d'une histoire (migration 257) ; jumeau de backend/utils/classesHistoire.js.
const CLASSES_HISTOIRE = ['CE1', 'CE2', 'CM1', 'CM2', '6ème', '5ème', '4ème', '3ème'];

const PLAN_LABELS = {
  free: 'Gratuit',
  premium: 'Premium',
  premium_plus: 'Premium+',
};

const TYPE_NIVEAU_OPTIONS = ['lecon', 'entrainement', 'boss', 'prologue'];
const DIFFICULTE_OPTIONS = ['facile', 'moyen', 'difficile'];
const DOMAINE_OPTIONS = ['grammaire', 'conjugaison', 'orthographe', 'vocabulaire', 'comprehension'];
// Doit rester d'accord avec les renderers réellement branchés dans
// `frontend/src/components/Scenario/ScenarioArena.tsx` (SCENARIO_RENDERERS) : un
// scénario codé mais absent de cette liste ne peut être posé sur un niveau que par un
// UPDATE SQL à la main — c'est ce qui a laissé `duel`, `fabrication` et `wagonnet_fou`
// injouables hors de la galerie de dev.
// ⚠️ `duel` reste rarement utile ici : un niveau de type `boss` prend le duel tout seul
// (cf. utils/adventureScenario), 'combat_monstre' valant « non précisé ».
const TYPE_SCENARIO_OPTIONS = [
  'combat_monstre', 'chemin_foret', 'examen_sage', 'infiltration',
  'crochetage', 'defense_zone', 'course', 'fabrication', 'wagonnet_fou', 'duel',
  'carrefour', 'pont_paires', 'avalanche', 'fuite', 'course_poursuite',
];

// Le tempo d'un niveau (PLAN_IMPL_TEMPO_SCENARIOS.md) : seuls ceux que le scénario sait
// jouer sont proposés ; « par défaut » laisse la colonne à NULL, c'est-à-dire le défaut du
// scénario — un niveau existant ne change donc jamais de rythme sans qu'on le décide.
const LIBELLES_TEMPO = { temps_reel: 'temps réel', libre: 'temps libre' };

// ─── Composant principal ──────────────────────────────────────────────────────

export default function AdventureScreen() {
  const [stories, setStories] = useState([]);
  const [scenarioCatalog, setScenarioCatalog] = useState(null);
  useEffect(() => { getAdminScenarios().then(setScenarioCatalog).catch(() => {}); }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Tabs
  const [activeTab, setActiveTab] = useState(0);

  // Sélection courante
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedBiome, setSelectedBiome] = useState(null);
  const [storyDetail, setStoryDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  // Dialogs
  const [storyDialog, setStoryDialog] = useState({ open: false, mode: 'create', data: {} });
  const [biomeDialog, setBiomeDialog] = useState({ open: false, mode: 'create', data: {} });
  const [levelDialog, setLevelDialog] = useState({ open: false, mode: 'create', data: {} });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, label: '' });
  const [auditDialog, setAuditDialog] = useState(false);
  const packInputRef = useRef(null);
  const [packBusy, setPackBusy] = useState(false);
  const [storyAssets, setStoryAssets] = useState([]);
  const [layoutEditor, setLayoutEditor] = useState({
    open: false,
    mode: null,
    biome: null,
    saving: false,
    publishing: false,
  });

  // ─── Chargement ──────────────────────────────────────────────────────────────

  const loadStories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminStories();
      const list = response?.stories || [];
      setStories(list);
      return list;
    } catch (err) {
      setError('Impossible de charger les histoires.');
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStoryDetail = useCallback(async (storyId) => {
    try {
      setLoadingDetail(true);
      const response = await getAdminStory(storyId);
      setStoryDetail(response?.story || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const loadAuditLog = useCallback(async (storyId) => {
    try {
      const response = await getAdminStoryAudit(storyId);
      setAuditLog(response?.log || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  /**
   * Collectibles proposables comme sceau de territoire achevé (migr. 111).
   * Chargés une fois : la liste ne dépend ni de la story ni du biome.
   */
  const [sealCandidates, setSealCandidates] = useState([]);
  useEffect(() => {
    getAdminSealCandidates()
      .then((r) => setSealCandidates(r?.items || []))
      .catch(() => setSealCandidates([]));
  }, []);

  const loadStoryAssets = useCallback(async (storyId) => {
    const res = await getAdminStoryAssets(storyId);
    const list = res?.assets || [];
    setStoryAssets(list);
    return list;
  }, []);

  useEffect(() => {
    if (selectedStory) {
      loadStoryDetail(selectedStory.id);
      loadStoryAssets(selectedStory.id).catch(() => setStoryAssets([]));
    } else {
      setStoryAssets([]);
    }
  }, [selectedStory, loadStoryDetail, loadStoryAssets]);

  const assetUrlForKey = useCallback(
    (assetKey) => {
      const matches = storyAssets.filter((a) => a.assetKey === assetKey);
      const entry =
        matches.find((a) => a.statut === 'published')
        || matches.find((a) => a.statut === 'pending')
        || matches[0];
      if (!entry?.fileUrl) return null;
      const path = entry.fileUrl.startsWith('/') ? entry.fileUrl.slice(1) : entry.fileUrl;
      return getAssetUrl(path.replace(/^assets\//, ''));
    },
    [storyAssets]
  );

  const openMapLayoutEditor = () => {
    setLayoutEditor({ open: true, mode: 'map', biome: null, saving: false, publishing: false });
  };

  const openBiomeLayoutEditor = (biome) => {
    setLayoutEditor({ open: true, mode: 'biome', biome, saving: false, publishing: false });
  };

  const handleSaveMapLayout = async (boxes, widthFactor) => {
    if (!storyDetail) return;
    setLayoutEditor((s) => ({ ...s, saving: true }));
    try {
      await updateAdminStoryPresentation(storyDetail.id, {
        mapBackgroundKey: storyDetail.presentation?.mapBackgroundKey || 'world_map',
        mapLayout: wrapLayout(boxes, { widthFactor }),
        version: storyDetail.presentation?.version || 1,
      });
      showSnack('Layout carte enregistré (brouillon)');
      await loadStoryDetail(storyDetail.id);
    } catch (err) {
      showSnack(err.message || 'Erreur de sauvegarde', 'error');
    } finally {
      setLayoutEditor((s) => ({ ...s, saving: false }));
    }
  };

  const handlePublishMapLayout = async () => {
    if (!storyDetail) return;
    setLayoutEditor((s) => ({ ...s, publishing: true }));
    try {
      await publishAdminStoryPresentation(storyDetail.id);
      showSnack('Présentation carte publiée — clients notifiés au prochain sync');
      await loadStoryDetail(storyDetail.id);
    } catch (err) {
      showSnack(err.message || 'Erreur de publication', 'error');
    } finally {
      setLayoutEditor((s) => ({ ...s, publishing: false }));
    }
  };

  const handleSaveBiomeLayout = async (boxes, widthFactor) => {
    const biome = layoutEditor.biome;
    if (!biome || !storyDetail) return;
    setLayoutEditor((s) => ({ ...s, saving: true }));
    try {
      await updateAdminBiomePresentation(biome.id, {
        backgroundKey: biome.presentation?.backgroundKey || biome.backgroundImage || `biome_${biome.ordre + 1}`,
        biomeLayout: wrapLayout(boxes, { widthFactor }),
        version: biome.presentation?.version || 1,
      });
      showSnack(`Layout « ${biome.nom} » enregistré (brouillon)`);
      await loadStoryDetail(storyDetail.id);
    } catch (err) {
      showSnack(err.message || 'Erreur de sauvegarde', 'error');
    } finally {
      setLayoutEditor((s) => ({ ...s, saving: false }));
    }
  };

  const handlePublishBiomeLayout = async () => {
    const biome = layoutEditor.biome;
    if (!biome || !storyDetail) return;
    setLayoutEditor((s) => ({ ...s, publishing: true }));
    try {
      await publishAdminBiomePresentation(biome.id);
      showSnack(`Présentation « ${biome.nom} » publiée`);
      await loadStoryDetail(storyDetail.id);
    } catch (err) {
      showSnack(err.message || 'Erreur de publication', 'error');
    } finally {
      setLayoutEditor((s) => ({ ...s, publishing: false }));
    }
  };

  const layoutEditorProps = useMemo(() => {
    if (!layoutEditor.open || !storyDetail) return null;

    if (layoutEditor.mode === 'map') {
      const pres = storyDetail.presentation;
      return {
        title: `Carte monde — ${storyDetail.titre}`,
        layout: pres?.mapLayout || wrapLayout([]),
        backgroundImageUrl: assetUrlForKey(`map_bg:${storyDetail.id}`),
        widthFactor: unwrapWidthFactor(pres?.mapLayout) || 1,
        isPublished: Boolean(pres?.isPublished),
        onSaveDraft: handleSaveMapLayout,
        onPublish: handlePublishMapLayout,
      };
    }

    if (layoutEditor.mode === 'biome' && layoutEditor.biome) {
      const biome = storyDetail.biomes?.find((b) => b.id === layoutEditor.biome.id) || layoutEditor.biome;
      const pres = biome.presentation;
      return {
        title: `Biome — ${biome.nom}`,
        layout: pres?.biomeLayout || wrapLayout([]),
        backgroundImageUrl: assetUrlForKey(`biome_bg:${biome.id}`),
        widthFactor: unwrapWidthFactor(pres?.biomeLayout) || (biome.id === 'foret_des_noms' ? 1.33 : 1),
        isPublished: Boolean(pres?.isPublished),
        onSaveDraft: handleSaveBiomeLayout,
        onPublish: handlePublishBiomeLayout,
      };
    }

    return null;
  }, [layoutEditor, storyDetail, assetUrlForKey, storyAssets]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleSelectStory = (story) => {
    setSelectedStory(story);
    setSelectedBiome(null);
    setActiveTab(0);
  };

  // ─── Actions STORY ────────────────────────────────────────────────────────────

  const handleSaveStory = async () => {
    const { mode, data } = storyDialog;
    try {
      if (mode === 'create') {
        await createAdminStory(data);
        showSnack('Histoire créée avec succès');
      } else {
        await updateAdminStory(data.id, data);
        showSnack('Histoire mise à jour');
      }
      setStoryDialog({ open: false, mode: 'create', data: {} });
      await loadStories();
      if (selectedStory?.id === data.id) await loadStoryDetail(data.id);
    } catch (err) {
      showSnack(err.message || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handlePublishStory = async (storyId) => {
    try {
      await publishAdminStory(storyId);
      showSnack('Histoire publiée');
      await loadStories();
      if (selectedStory?.id === storyId) await loadStoryDetail(storyId);
    } catch (err) {
      showSnack(err.message || 'Erreur lors de la publication', 'error');
    }
  };

  const handleArchiveStory = async (storyId) => {
    try {
      await archiveAdminStory(storyId);
      showSnack('Histoire archivée');
      await loadStories();
      if (selectedStory?.id === storyId) await loadStoryDetail(storyId);
    } catch (err) {
      showSnack(err.message || 'Erreur lors de l\'archivage', 'error');
    }
  };

  const handleExportPack = async (storyId) => {
    setPackBusy(true);
    try {
      const { blob, filename } = await exportAdminStoryPack(storyId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      showSnack('Pack téléchargé — prêt pour Git');
    } catch (err) {
      showSnack(err.message || 'Erreur export pack', 'error');
    } finally {
      setPackBusy(false);
    }
  };

  /** Ce que l'import désactiverait, en une phrase par sorte ; vide s'il ne retire rien. */
  const retraitsDuRapport = (rapport) => {
    if (!rapport) return '';
    const lignes = [];
    const { niveaux, biomesDesactives, embranchements, enfantsSurNiveauxDesactives } = rapport;
    if (niveaux?.desactives?.length) {
      lignes.push(`Niveaux absents du pack, désactivés : ${niveaux.desactives.join(', ')}`
        + (enfantsSurNiveauxDesactives ? ` (${enfantsSurNiveauxDesactives} enfant(s) y ont joué ; leur progression est gardée)` : ''));
    }
    if (biomesDesactives?.length) lignes.push(`Biomes désactivés : ${biomesDesactives.join(', ')}`);
    if (embranchements?.desactives?.length) {
      lignes.push(`Embranchements désactivés : ${embranchements.desactives.join(', ')} (choix des enfants gardés)`);
    }
    return lignes.join('\n');
  };

  const handleImportPackFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setPackBusy(true);
    try {
      // D'abord un essai : l'import met à jour en place, mais ce que le pack ne contient plus
      // est désactivé — l'adulte le voit avant que ça touche les enfants.
      const essai = await importAdminStoryPack(file, { essai: true });
      const retraits = retraitsDuRapport(essai.rapport);
      if (retraits && !window.confirm(`${retraits}\n\nImporter quand même ?`)) {
        showSnack('Import annulé', 'info');
        return;
      }
      const res = await importAdminStoryPack(file);
      showSnack(`Histoire ${res.rapport?.histoire === 'creee' ? 'créée' : 'mise à jour'} : ${res.storyId}`
        + ` (${res.rapport?.niveaux?.ajoutes?.length ?? 0} niveau(x) ajouté(s), `
        + `${res.rapport?.niveaux?.misAJour ?? 0} mis à jour ; progression des enfants gardée)`);
      const list = await loadStories();
      const imported = list.find((s) => s.id === res.storyId);
      setSelectedStory(imported ?? { id: res.storyId, titre: res.storyId });
      await loadStoryDetail(res.storyId);
    } catch (err) {
      showSnack(err.message || 'Erreur import pack', 'error');
    } finally {
      setPackBusy(false);
    }
  };

  // ─── Actions BIOME ────────────────────────────────────────────────────────────

  const handleSaveBiome = async () => {
    const { mode, data } = biomeDialog;
    try {
      if (mode === 'create') {
        await createAdminBiome({ ...data, storyId: selectedStory?.id });
        showSnack('Biome créé');
      } else {
        await updateAdminBiome(data.id, data);
        showSnack('Biome mis à jour');
      }
      setBiomeDialog({ open: false, mode: 'create', data: {} });
      if (selectedStory) await loadStoryDetail(selectedStory.id);
    } catch (err) {
      showSnack(err.message || 'Erreur lors de la sauvegarde du biome', 'error');
    }
  };

  const handleDeleteBiome = async (biomeId) => {
    try {
      await deleteAdminBiome(biomeId);
      showSnack('Biome désactivé');
      if (selectedStory) await loadStoryDetail(selectedStory.id);
    } catch (err) {
      showSnack(err.message || 'Erreur', 'error');
    }
  };

  // ─── Actions NIVEAU ───────────────────────────────────────────────────────────

  /**
   * Les décors du scénario sélectionné (`shared_config.SCENARIO_THEMES`, copie
   * synchronisée de la source frontend). Un scénario sans décor déclaré laisse le champ
   * désactivé plutôt qu'ouvert sur du vide.
   */
  const selectedScenario = levelDialog.data.typeScenario || 'combat_monstre';
  const builtins = scenarioCatalog?.scenarios.find(s => s.id === selectedScenario);
  const publishedVariants = scenarioCatalog?.variants.filter(v => v.typeScenario === selectedScenario && v.published) || [];
  const themesDuScenario = [...(builtins ? Object.keys(builtins.themes) : getScenarioThemes(selectedScenario)), ...publishedVariants.map(v => v.id)];
  const capaciteTempo = getScenarioTempos(levelDialog.data.typeScenario || 'combat_monstre');

  const handleSaveLevel = async () => {
    const { mode, data } = levelDialog;
    try {
      if (mode === 'create') {
        await createAdminLevel({ ...data, biomeId: selectedBiome?.id });
        showSnack('Niveau créé');
      } else {
        await updateAdminLevel(data.id, data);
        showSnack('Niveau mis à jour');
      }
      setLevelDialog({ open: false, mode: 'create', data: {} });
      if (selectedStory) await loadStoryDetail(selectedStory.id);
    } catch (err) {
      showSnack(err.message || 'Erreur lors de la sauvegarde du niveau', 'error');
    }
  };

  const handleDeleteLevel = async (levelId) => {
    try {
      await deleteAdminLevel(levelId);
      showSnack('Niveau désactivé');
      if (selectedStory) await loadStoryDetail(selectedStory.id);
    } catch (err) {
      showSnack(err.message || 'Erreur', 'error');
    }
  };

  // ─── Rendu ────────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            🗺️ Gestion des histoires d'aventure
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              ref={packInputRef}
              type="file"
              accept=".zip,.dicto-story-pack.zip"
              hidden
              onChange={handleImportPackFile}
            />
            <IconButton onClick={loadStories} size="small" disabled={packBusy}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={packBusy}
              onClick={() => packInputRef.current?.click()}
            >
              Importer un pack
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={packBusy}
              onClick={() => setStoryDialog({ open: true, mode: 'create', data: { statut: 'draft', requiredPlan: 'free', ordre: stories.length } })}
            >
              Nouvelle histoire
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* ── Colonne gauche : liste des stories ─────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Histoires ({stories.length})
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              stories.map((story) => (
                <Card
                  key={story.id}
                  sx={{
                    mb: 1.5,
                    cursor: 'pointer',
                    border: selectedStory?.id === story.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': { boxShadow: 3 },
                  }}
                  onClick={() => handleSelectStory(story)}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
                        {story.titre}
                      </Typography>
                      <Chip
                        label={STATUT_LABELS[story.statut] || story.statut}
                        color={STATUT_COLORS[story.statut] || 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {story.biomesCount || 0} biomes · {story.levelsCount || 0} niveaux
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label={`Ordre: ${story.ordre}`} size="small" variant="outlined" />
                      <Chip label={PLAN_LABELS[story.requiredPlan] || story.requiredPlan} size="small" variant="outlined" />
                      {(story.classeMin || story.classeMax) && (
                        <Chip label={`${story.classeMin || '…'} → ${story.classeMax || '…'}`} size="small" variant="outlined" />
                      )}
                      {story.requiredPlan !== 'free' && story.niveauxEssai > 0 && (
                        <Chip label={`Essai : ${story.niveauxEssai} niv.`} size="small" variant="outlined" color="info" />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setStoryDialog({ open: true, mode: 'edit', data: { ...story } }); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {story.statut === 'draft' && (
                      <Tooltip title="Publier">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDialog({ open: true, label: `Publier "${story.titre}" ?`, action: () => handlePublishStory(story.id) });
                          }}
                        >
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {story.statut === 'published' && (
                      <Tooltip title="Archiver">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDialog({ open: true, label: `Archiver "${story.titre}" ?`, action: () => handleArchiveStory(story.id) });
                          }}
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Exporter le pack (Git)">
                      <IconButton
                        size="small"
                        disabled={packBusy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleExportPack(story.id);
                        }}
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Audit">
                      <IconButton
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await loadAuditLog(story.id);
                          setAuditDialog(true);
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              ))
            )}
          </Grid>

          {/* ── Colonne droite : détail story ──────────────────────────────── */}
          <Grid item xs={12} md={8}>
            {!selectedStory ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, color: 'text.secondary' }}>
                <MapIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography>Sélectionnez une histoire pour voir son contenu</Typography>
              </Box>
            ) : loadingDetail ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : storyDetail ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {storyDetail.titre}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      disabled={packBusy}
                      onClick={() => void handleExportPack(storyDetail.id)}
                    >
                      Exporter le pack
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => setBiomeDialog({ open: true, mode: 'create', data: { ordre: (storyDetail.biomes?.length || 0), icone: '🗺️' } })}
                    >
                      Ajouter un biome
                    </Button>
                  </Box>
                </Box>

                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                  <Tab label={`Biomes (${storyDetail.biomes?.length || 0})`} icon={<TerrainIcon />} iconPosition="start" />
                  <Tab label="Présentation" icon={<MapIcon />} iconPosition="start" />
                  <Tab label="Assets" icon={<ImageIcon />} iconPosition="start" />
                </Tabs>

                {/* ── Tab 0 : Biomes & Niveaux ───────────────────────── */}
                {activeTab === 0 && (
                  <Box>
                    {(storyDetail.biomes || []).map((biome) => (
                      <Accordion key={biome.id} defaultExpanded={false}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography fontSize="1.4rem">{biome.icone || '🗺️'}</Typography>
                            <Typography fontWeight="bold">{biome.nom}</Typography>
                            <Chip label={`ordre ${biome.ordre}`} size="small" variant="outlined" />
                            <Chip label={`${(biome.niveaux || []).length} niveaux`} size="small" />
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setBiomeDialog({ open: true, mode: 'edit', data: { ...biome } }); }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDialog({ open: true, label: `Désactiver le biome "${biome.nom}" ?`, action: () => handleDeleteBiome(biome.id) });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Layout : {layoutBoxCount(biome.presentation?.biomeLayout)} boîte(s)
                                {biome.presentation?.isPublished ? ' · publiée' : biome.presentation ? ' · brouillon' : ' · absent'}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LayoutIcon />}
                              onClick={() => openBiomeLayoutEditor(biome)}
                            >
                              Éditer le layout
                            </Button>
                          </Box>
                          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => { setSelectedBiome(biome); setLevelDialog({ open: true, mode: 'create', data: { difficulte: 'facile', type: 'lecon', domaine: 'grammaire', typeScenario: 'combat_monstre', ordre: (biome.niveaux?.length || 0) + 1 } }); }}
                            >
                              Ajouter un niveau
                            </Button>
                          </Box>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Ordre</TableCell>
                                  <TableCell>Nom</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Difficulté</TableCell>
                                  <TableCell>Scénario</TableCell>
                                  <TableCell>XP</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(biome.niveaux || []).map((niveau) => (
                                  <TableRow key={niveau.id} hover>
                                    <TableCell>{niveau.ordre}</TableCell>
                                    <TableCell>{niveau.nom}</TableCell>
                                    <TableCell>
                                      <Chip label={niveau.type} size="small" />
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={DIFFICULTE_LABELS[niveau.difficulte] || niveau.difficulte} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">{niveau.typeScenario || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>{niveau.recompenseXp}</TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        onClick={() => { setSelectedBiome(biome); setLevelDialog({ open: true, mode: 'edit', data: { ...niveau } }); }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setConfirmDialog({ open: true, label: `Désactiver le niveau "${niveau.nom}" ?`, action: () => handleDeleteLevel(niveau.id) })}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {(biome.niveaux || []).length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={7} align="center">
                                      <Typography variant="body2" color="text.secondary">Aucun niveau</Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    {(storyDetail.biomes || []).length === 0 && (
                      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography>Aucun biome. Créez le premier biome de cette histoire.</Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* ── Tab 1 : Présentation ────────────────────────────── */}
                {activeTab === 1 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      Présentation de la carte monde
                    </Typography>
                    {storyDetail.presentation ? (
                      <Box sx={{ mb: 2 }}>
                        <Chip label={`Background: ${storyDetail.presentation.mapBackgroundKey || '(non défini)'}`} sx={{ mb: 1, mr: 1 }} />
                        <Chip
                          label={`${layoutBoxCount(storyDetail.presentation.mapLayout)} boîtes · v${storyDetail.presentation.version}`}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="caption" display="block" color={storyDetail.presentation.isPublished ? 'success.main' : 'warning.main'}>
                          {storyDetail.presentation.isPublished ? '✓ Publiée (visible par les clients)' : '⚠ Brouillon — publier pour mettre en ligne'}
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Aucune présentation en base. Ouvrez l&apos;éditeur pour créer le layout — il sera enregistré directement en BDD.
                      </Alert>
                    )}
                    <Button variant="contained" startIcon={<LayoutIcon />} onClick={openMapLayoutEditor}>
                      Éditer le layout carte
                    </Button>
                  </Box>
                )}

                {/* ── Tab 2 : Assets ──────────────────────────────────── */}
                {activeTab === 2 && (
                  <AdventureStoryAssetsPanel
                    storyId={storyDetail.id}
                    biomes={storyDetail.biomes || []}
                    onRefresh={() => loadStoryAssets(storyDetail.id)}
                    onNotify={showSnack}
                  />
                )}
              </>
            ) : null}
          </Grid>
        </Grid>

        {/* ── Dialog Story ───────────────────────────────────────────────────── */}
        <Dialog open={storyDialog.open} onClose={() => setStoryDialog({ open: false, mode: 'create', data: {} })} maxWidth="sm" fullWidth>
          <DialogTitle>{storyDialog.mode === 'create' ? 'Nouvelle histoire' : 'Modifier l\'histoire'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {storyDialog.mode === 'create' && (
                <TextField
                  label="ID (unique, ex: histoire_mer)"
                  value={storyDialog.data.id || ''}
                  onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, id: e.target.value } }))}
                  fullWidth size="small"
                />
              )}
              <TextField
                label="Titre"
                value={storyDialog.data.titre || ''}
                onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, titre: e.target.value } }))}
                fullWidth size="small" required
              />
              <TextField
                label="Description"
                value={storyDialog.data.description || ''}
                onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, description: e.target.value } }))}
                fullWidth multiline rows={3} size="small"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={storyDialog.data.statut || 'draft'}
                      label="Statut"
                      onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, statut: e.target.value } }))}
                    >
                      <MenuItem value="draft">Brouillon</MenuItem>
                      <MenuItem value="published">Publiée</MenuItem>
                      <MenuItem value="archived">Archivée</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Plan requis</InputLabel>
                    <Select
                      value={storyDialog.data.requiredPlan || 'free'}
                      label="Plan requis"
                      onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, requiredPlan: e.target.value } }))}
                    >
                      <MenuItem value="free">Gratuit</MenuItem>
                      <MenuItem value="premium">Premium</MenuItem>
                      <MenuItem value="premium_plus">Premium+</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Niveaux d'essai (gratuits)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={storyDialog.data.niveauxEssai ?? 0}
                    onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, niveauxEssai: Math.max(0, parseInt(e.target.value) || 0) } }))}
                    disabled={(storyDialog.data.requiredPlan || 'free') === 'free'}
                    helperText="Premiers niveaux jouables ouverts à tous (hors prologues). 0 = histoire fermée sans le plan."
                    fullWidth size="small"
                  />
                </Grid>
                {/* Public visé (migration 257) : montré au parent (portail, boutique) et dans la
                    bibliothèque. Une indication : les exercices suivent la classe de l'enfant. */}
                {[['classeMin', 'Classe minimale'], ['classeMax', 'Classe maximale']].map(([cle, libelle]) => (
                  <Grid item xs={6} key={cle}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{libelle}</InputLabel>
                      <Select
                        value={storyDialog.data[cle] || ''}
                        label={libelle}
                        onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, [cle]: e.target.value || null } }))}
                      >
                        <MenuItem value="">Toutes</MenuItem>
                        {CLASSES_HISTOIRE.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
                <Grid item xs={6}>
                  <TextField
                    label="Ordre"
                    type="number"
                    value={storyDialog.data.ordre ?? 0}
                    onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, ordre: parseInt(e.target.value) || 0 } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Difficulté recommandée</InputLabel>
                    <Select
                      value={storyDialog.data.difficulteRecommandee || ''}
                      label="Difficulté recommandée"
                      onChange={(e) => setStoryDialog(d => ({ ...d, data: { ...d.data, difficulteRecommandee: e.target.value } }))}
                    >
                      <MenuItem value="">Non spécifiée</MenuItem>
                      <MenuItem value="facile">Facile</MenuItem>
                      <MenuItem value="moyen">Moyen</MenuItem>
                      <MenuItem value="difficile">Difficile</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStoryDialog({ open: false, mode: 'create', data: {} })}>Annuler</Button>
            <Button onClick={handleSaveStory} variant="contained">
              {storyDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog Biome ───────────────────────────────────────────────────── */}
        <Dialog open={biomeDialog.open} onClose={() => setBiomeDialog({ open: false, mode: 'create', data: {} })} maxWidth="sm" fullWidth>
          <DialogTitle>{biomeDialog.mode === 'create' ? 'Nouveau biome' : 'Modifier le biome'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {biomeDialog.mode === 'create' && (
                <TextField
                  label="ID (ex: foret_des_sorts)"
                  value={biomeDialog.data.id || ''}
                  onChange={(e) => setBiomeDialog(d => ({ ...d, data: { ...d.data, id: e.target.value } }))}
                  fullWidth size="small" required
                />
              )}
              <TextField
                label="Nom"
                value={biomeDialog.data.nom || ''}
                onChange={(e) => setBiomeDialog(d => ({ ...d, data: { ...d.data, nom: e.target.value } }))}
                fullWidth size="small" required
              />
              <TextField
                label="Description"
                value={biomeDialog.data.description || ''}
                onChange={(e) => setBiomeDialog(d => ({ ...d, data: { ...d.data, description: e.target.value } }))}
                fullWidth multiline rows={2} size="small"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Icône (emoji)"
                    value={biomeDialog.data.icone || ''}
                    onChange={(e) => setBiomeDialog(d => ({ ...d, data: { ...d.data, icone: e.target.value } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Ordre"
                    type="number"
                    value={biomeDialog.data.ordre ?? 0}
                    onChange={(e) => setBiomeDialog(d => ({ ...d, data: { ...d.data, ordre: parseInt(e.target.value) || 0 } }))}
                    fullWidth size="small"
                  />
                </Grid>
              </Grid>
              {/*
                Sceau du territoire achevé : l'objet que ce biome rapporte, affiché
                sur la carte du monde une fois le biome fini. Sans lien, le jeu pose
                la coche des niveaux terminés — c'est un choix valable, pas un trou
                à combler. Auparavant le sceau se déduisait de la POSITION du biome,
                ce qui donnait le Fragment de la Forêt au premier biome de toute
                autre campagne.
              */}
              <TextField
                select
                label="Sceau du territoire achevé"
                helperText="Vide → coche générique (niveau terminé)"
                value={biomeDialog.data.sealItemId || ''}
                onChange={(e) => setBiomeDialog(d => ({
                  ...d,
                  data: { ...d.data, sealItemId: e.target.value || null },
                }))}
                fullWidth size="small"
              >
                <MenuItem value=""><em>Aucun — coche générique</em></MenuItem>
                {sealCandidates.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} {item.subType ? `(${item.subType})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBiomeDialog({ open: false, mode: 'create', data: {} })}>Annuler</Button>
            <Button onClick={handleSaveBiome} variant="contained">
              {biomeDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog Niveau ──────────────────────────────────────────────────── */}
        <Dialog open={levelDialog.open} onClose={() => setLevelDialog({ open: false, mode: 'create', data: {} })} maxWidth="md" fullWidth>
          <DialogTitle>
            {levelDialog.mode === 'create' ? 'Nouveau niveau' : 'Modifier le niveau'}{selectedBiome ? ` — ${selectedBiome.nom}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {levelDialog.mode === 'create' && (
                <TextField
                  label="ID (ex: sort_1)"
                  value={levelDialog.data.id || ''}
                  onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, id: e.target.value } }))}
                  fullWidth size="small" required
                />
              )}
              <TextField
                label="Nom"
                value={levelDialog.data.nom || ''}
                onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, nom: e.target.value } }))}
                fullWidth size="small" required
              />
              <TextField
                label="Description"
                value={levelDialog.data.description || ''}
                onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, description: e.target.value } }))}
                fullWidth size="small"
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select value={levelDialog.data.type || 'lecon'} label="Type"
                      onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, type: e.target.value } }))}>
                      {TYPE_NIVEAU_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Difficulté</InputLabel>
                    <Select value={levelDialog.data.difficulte || 'facile'} label="Difficulté"
                      onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, difficulte: e.target.value } }))}>
                      {DIFFICULTE_OPTIONS.map(d => <MenuItem key={d} value={d}>{DIFFICULTE_LABELS[d]}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Domaine</InputLabel>
                    <Select value={levelDialog.data.domaine || 'grammaire'} label="Domaine"
                      onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, domaine: e.target.value } }))}>
                      {DOMAINE_OPTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Compétence (optionnel)"
                    value={levelDialog.data.competence || ''}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, competence: e.target.value } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Notion (optionnel)"
                    value={levelDialog.data.notion || ''}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, notion: e.target.value } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Scénario</InputLabel>
                    <Select value={levelDialog.data.typeScenario || 'combat_monstre'} label="Scénario"
                      onChange={(e) => setLevelDialog(d => ({
                        ...d,
                        // ⚠️ Changer de scénario VIDE le décor : un thème appartient à une
                        // mise en scène, et celui d'une autre retombe en silence sur le
                        // décor par défaut. C'est ce qui est arrivé au premier boss du jeu,
                        // resté sur un thème de combat après son passage en duel.
                        // Le tempo aussi : un tempo appartient à ce que le scénario sait jouer.
                        data: { ...d.data, typeScenario: e.target.value, themeVisuel: '', tempo: null },
                      }))}>
                      {TYPE_SCENARIO_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small" disabled={themesDuScenario.length === 0}>
                    <InputLabel>Décor</InputLabel>
                    <Select
                      value={themesDuScenario.includes(levelDialog.data.themeVisuel) ? levelDialog.data.themeVisuel : ''}
                      label="Décor"
                      onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, themeVisuel: e.target.value || null } }))}
                    >
                      <MenuItem value=""><em>par défaut</em></MenuItem>
                      {themesDuScenario.map(th => <MenuItem key={th} value={th}>{publishedVariants.find(v => v.id === th)?.published?.label || th}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small" disabled={capaciteTempo.tempos.length < 2}>
                    <InputLabel>Tempo</InputLabel>
                    <Select
                      value={capaciteTempo.tempos.includes(levelDialog.data.tempo) ? levelDialog.data.tempo : ''}
                      label="Tempo"
                      onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, tempo: e.target.value || null } }))}
                    >
                      <MenuItem value=""><em>par défaut ({LIBELLES_TEMPO[capaciteTempo.defaut]})</em></MenuItem>
                      {capaciteTempo.tempos.map(t => <MenuItem key={t} value={t}>{LIBELLES_TEMPO[t]}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="XP" type="number"
                    value={levelDialog.data.recompenseXp ?? 25}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, recompenseXp: parseInt(e.target.value) || 25 } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Pièces" type="number"
                    value={levelDialog.data.recompensePieces ?? 10}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, recompensePieces: parseInt(e.target.value) || 10 } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Texte narratif (histoire)"
                    value={levelDialog.data.histoire || ''}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, histoire: e.target.value } }))}
                    fullWidth multiline rows={4} size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Ordre" type="number"
                    value={levelDialog.data.ordre ?? 1}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, ordre: parseInt(e.target.value) || 1 } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="Position X (%)" type="number"
                    value={levelDialog.data.positionX ?? 50}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, positionX: parseInt(e.target.value) || 0 } }))}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField label="Position Y (%)" type="number"
                    value={levelDialog.data.positionY ?? 50}
                    onChange={(e) => setLevelDialog(d => ({ ...d, data: { ...d.data, positionY: parseInt(e.target.value) || 0 } }))}
                    fullWidth size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLevelDialog({ open: false, mode: 'create', data: {} })}>Annuler</Button>
            <Button onClick={handleSaveLevel} variant="contained">
              {levelDialog.mode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog Confirmation ────────────────────────────────────────────── */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, label: '' })} maxWidth="xs">
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.label}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, action: null, label: '' })}>Annuler</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (confirmDialog.action) confirmDialog.action();
                setConfirmDialog({ open: false, action: null, label: '' });
              }}
            >
              Confirmer
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog Audit ───────────────────────────────────────────────────── */}
        <Dialog open={auditDialog} onClose={() => setAuditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Journal d'audit</DialogTitle>
          <DialogContent dividers>
            {auditLog.length === 0 ? (
              <Typography color="text.secondary">Aucune entrée d'audit.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Effectuée par</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLog.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(entry.performedAt).toLocaleString('fr-FR')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={entry.action} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{entry.performedBy}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAuditDialog(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        {layoutEditorProps ? (
          <BoxPositionnerEditor
            open={layoutEditor.open}
            onClose={() => setLayoutEditor((s) => ({ ...s, open: false }))}
            title={layoutEditorProps.title}
            layout={layoutEditorProps.layout}
            backgroundImageUrl={layoutEditorProps.backgroundImageUrl}
            widthFactor={layoutEditorProps.widthFactor}
            isPublished={layoutEditorProps.isPublished}
            onSaveDraft={layoutEditorProps.onSaveDraft}
            onPublish={layoutEditorProps.onPublish}
            saving={layoutEditor.saving}
            publishing={layoutEditor.publishing}
          />
        ) : null}

        {/* ── Snackbar ───────────────────────────────────────────────────────── */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
}
